<script setup lang="ts">
import { computed, ref } from 'vue'
import { Check, Copy } from 'lucide-vue-next'
import { useToast } from '../composables/useToast'

const props = withDefaults(defineProps<{
    value?: string | null
    kind?: 'email' | 'phone'
    /** Render only the copy icon (use next to an existing link). */
    iconOnly?: boolean
}>(), { value: '', kind: 'email', iconOnly: false })

const PLACEHOLDER = '-'
const LABELS = { email: 'Email', phone: 'Phone number' } as const

const toast = useToast()
const copied = ref(false)

const text = computed(() => (props.value ?? '').trim())
const copyable = computed(() => text.value !== '' && text.value !== PLACEHOLDER)
const label = computed(() => LABELS[props.kind])

const copy = async () => {
    try {
        await navigator.clipboard.writeText(text.value)
        copied.value = true
        setTimeout(() => { copied.value = false }, 1500)
        toast.success(`${label.value} copied`)
    } catch {
        toast.error(`Couldn't copy ${label.value.toLowerCase()}. Select the text to copy it manually.`)
    }
}
</script>

<template>
    <button
        v-if="copyable"
        type="button"
        :aria-label="`Copy ${label.toLowerCase()} ${text}`"
        :title="`Copy ${label.toLowerCase()}`"
        class="group inline-flex items-center gap-1.5 max-w-full rounded cursor-pointer hover:text-cc-text hover:bg-cc-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-cc-primary"
        :class="iconOnly ? 'p-1 shrink-0' : 'px-1 -mx-1'"
        @click="copy"
    >
        <span v-if="!iconOnly" class="truncate">{{ text }}</span>
        <Check v-if="copied" :size="14" class="shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
        <Copy v-else :size="14" class="shrink-0 opacity-50 group-hover:opacity-100 group-focus-visible:opacity-100" aria-hidden="true" />
    </button>
    <span v-else-if="!iconOnly">{{ PLACEHOLDER }}</span>
</template>
